import { Injectable } from "@angular/core"
import { Observable, catchError, of } from "rxjs"
import { NewRelicService } from "../new-relic/new-relic.service"
import { ErrorLoggingLevel } from "src/app/enums/error-logging-level.enum"
import { AuthenticationService } from "../auth/authentication.service"
import { SupportType } from "src/app/models/common/SupportType"
import { SupportService } from "../support/support.service"

export interface HshdDetection {
  timestamp: string
  consecutiveDigits: number
  hshdType: string
  formField: string
  formId: string
}

@Injectable({
  providedIn: "root",
})
export class HshdAlertService {

  constructor(
    private newRelicService: NewRelicService,
    private authService: AuthenticationService,
    private supportService: SupportService
  ) {}

  /**
   * Log HSHD detection and trigger PagerDuty alert
   */
  logHshdDetection(detection: HshdDetection): void {
    // Log to New Relic for monitoring
    this.logToNewRelic(detection)

    // Trigger PagerDuty alert via OL /support endpoint
    this.triggerPagerDutyAlert(detection).subscribe()
  }

  /**
   * Log HSHD detection to New Relic for dashboards and alerting
   */
  private logToNewRelic(detection: HshdDetection): void {
    const errorMessage = `HSHD Detected: ${detection.hshdType} in ${detection.formField} (${detection.consecutiveDigits} digits)`
    
    this.newRelicService.logCustomError(errorMessage, {
      logLevel: ErrorLoggingLevel.Info,
      event: "HSHD_DETECTION",
      hshdType: detection.hshdType,
      consecutiveDigits: String(detection.consecutiveDigits),
      formField: detection.formField,
      formId: detection.formId,
      timestamp: detection.timestamp,
    })
  }

  /**
   * Trigger PagerDuty alert via OL /support endpoint.
   * Logs failure to New Relic if the call fails, but does not throw.
   */
  private triggerPagerDutyAlert(detection: HshdDetection): Observable<any> {
    const requestId = this.authService.getTrackingCookie() || "unknown"
    return this.supportService.submitSupport$(SupportType.HSHD_DETECTION, requestId).pipe(
      catchError((error) => {
        this.newRelicService.logCustomError("PagerDuty alert failed for HSHD detection", {
          logLevel: ErrorLoggingLevel.Error,
          event: "PAGERDUTY_ALERT_FAILURE",
          hshdType: detection.hshdType,
          formField: detection.formField,
          formId: detection.formId,
          errorMessage: error instanceof Error ? error.message : String(error),
        })
        return of(null)
      })
    )
  }
}
